import { useState, useRef, useEffect } from "react";
import { Moon, Sun, Monitor, Palette, Check } from "lucide-react";
import clsx from "clsx";
import { useTheme, PALETTES } from "@/lib/theme";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function ThemeMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { palette, mode, resolvedMode, setPalette, setMode } = useTheme();
  const { user, setUser } = useAuth();

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const persist = (next: { palette?: string; mode?: string }) => {
    if (!user) return;
    const theme = { palette: next.palette ?? palette, mode: next.mode ?? mode };
    api.put<{ user: any }>("/auth/preferences", { theme }).then((r) => r.user && setUser(r.user)).catch(() => {});
  };

  return (
    <div className="relative" ref={ref}>
      <button className="btn-ghost !p-2" onClick={() => setOpen((o) => !o)} aria-label="Appearance">
        {resolvedMode === "dark" ? <Moon size={18} /> : <Sun size={18} />}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-64 animate-fade-in rounded-xl border border-line bg-surface p-3 shadow-pop">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Mode</p>
          <div className="grid grid-cols-3 gap-1.5">
            {([["light", Sun], ["dark", Moon], ["system", Monitor]] as const).map(([m, Icon]) => (
              <button
                key={m}
                onClick={() => { setMode(m); persist({ mode: m }); }}
                className={clsx("flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs capitalize",
                  mode === m ? "border-brand-500 bg-brand-50 text-brand-700 dark:text-brand-300" : "border-line hover:bg-surface-2")}
              >
                <Icon size={15} /> {m}
              </button>
            ))}
          </div>
          <p className="mb-2 mt-4 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
            <Palette size={12} /> Theme
          </p>
          <div className="space-y-1">
            {PALETTES.map((p) => (
              <button
                key={p.id}
                onClick={() => { setPalette(p.id); persist({ palette: p.id }); }}
                className={clsx("flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2",
                  palette === p.id && "font-semibold")}
              >
                <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: p.swatch }} />
                <span className="flex-1 text-left">{p.label}</span>
                {palette === p.id && <Check size={14} className="text-brand-600" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
